import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Heart, 
  Clock, 
  Users, 
  Star, 
  Edit3, 
  MoreVertical, 
  Play, 
  Trophy, 
  Bookmark,
  Timer,
  Target,
  PoundSterling,
  RefreshCw
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import gameService from '../services/gameService';

// Helper function to check if game is co-op based on isCoOp field OR tags
const isCoOpGame = (game) => {
  // Check the isCoOp field first
  if (game?.isCoOp === true) return true;
  
  // Also check tags for cooperative keywords
  const tags = game?.tags || [];
  if (!Array.isArray(tags)) return false;
  const coopKeywords = ['cooperative', 'co-op', 'cooperative game'];
  return tags.some(tag => 
    coopKeywords.some(keyword => 
      tag.toLowerCase().includes(keyword)
    )
  );
};

const GameCard = ({ game, onEdit, onToggleOwned, onToggleTry, onPlay }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [priceCooldown, setPriceCooldown] = useState(null);
  const [skipNextLoad, setSkipNextLoad] = useState(false);
  
  // Check if game is co-op based on isCoOp field or tags - memoized
  const isCoOp = useMemo(() => isCoOpGame(game), [game.isCoOp, game.tags]);

  // PERFORMANCE: Disable auto-loading of prices on mount to prevent API spam
  // Prices will only load when user explicitly clicks the refresh button
  // This prevents 200+ API calls on initial page load
  useEffect(() => {
    // Skip if we just manually updated
    if (skipNextLoad) {
      setSkipNextLoad(false);
      return;
    }
    
    // Clear price data if game no longer meets criteria
    const shouldShowPrice = game.wishlist || game.try;
    if (!shouldShowPrice) {
      setPriceData(null);
      setPriceCooldown(null);
    }
    // NOTE: Removed automatic loadPriceData() call - now manual only
  }, [game.id, game.bggId, game.wishlist, game.try, skipNextLoad]);

  const loadPriceData = async () => {
    try {
      const price = await gameService.getGamePrice(game.id);
      setPriceData(price);
      
      if (price) {
        // Check cooldown status
        const cooldown = await gameService.checkPriceCooldown(game.id);
        setPriceCooldown(cooldown);
      }
    } catch (error) {
      console.error('Error loading price data:', error);
    }
  };

  const handleUpdatePrice = async () => {
    if (!game.bggId || isLoadingPrice) return;
    
    setIsLoadingPrice(true);
    
    try {
      const result = await gameService.updateGamePrice(game.id, game.bggId);
      
      if (result?.success && result?.price_data) {
        // Prevent the useEffect from immediately reloading old data
        setSkipNextLoad(true);
        
        // Force a new object reference to ensure React detects the change
        const newPriceData = { ...result.price_data };
        setPriceData(newPriceData);
        
        // Update cooldown with error handling
        try {
          const cooldown = await gameService.checkPriceCooldown(game.id);
          setPriceCooldown(cooldown);
        } catch (cooldownError) {
          console.error('Error checking price cooldown:', cooldownError);
        }
      } else {
        // Log error but don't break the UI
        console.warn('Price update failed:', result?.message || 'No price data received');
      }
    } catch (error) {
      console.error('Error updating price:', error);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const shouldShowPriceSection = (game.wishlist || game.try) && game.bggId;

  // Memoize complexity dots calculation
  const complexityDots = useMemo(() => {
    const dots = [];
    const maxDots = 5;
    const filledDots = Math.round(game.complexity);
    
    for (let i = 1; i <= maxDots; i++) {
      const isFilled = i <= filledDots;
      dots.push(
        <div
          key={i}
          className={`w-2 h-2 rounded-full transition-colors ${
            isFilled 
              ? game.complexity <= 2 
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : game.complexity <= 3 
                ? 'bg-amber-500 dark:bg-amber-400'
                : 'bg-red-500 dark:bg-red-400'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
        />
      );
    }
    return dots;
  }, [game.complexity]);

  const getComplexityDots = (complexity) => complexityDots;

  // Memoize status info
  const statusInfo = useMemo(() => {
    if (game.owned) return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
    if (game.wishlist) return { 
      text: 'Wishlist', 
      color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700', 
      icon: Bookmark 
    };
    if (game.try) return { 
      text: 'Want to Try', 
      color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700', 
      icon: Target 
    };
    // Default to owned if no status is set
    return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
  }, [game.owned, game.wishlist, game.try]);

  const getStatusInfo = () => statusInfo;
  const StatusIcon = statusInfo.icon;

  return (
    <Card 
      className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-300 hover:shadow-xl hover:shadow-orange-100 dark:hover:shadow-orange-900/20 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Game Image */}
      <div className="relative h-48 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-700 dark:to-gray-600 overflow-hidden">
        {game.image && (game.image.startsWith('http') || game.image.startsWith('data:')) && !game.image.includes('placeholder') ? (
          <img 
            src={game.image} 
            alt={game.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ minHeight: '192px' }}
            onError={(e) => {
              // If image fails to load, hide it and show dice placeholder
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 dark:from-gray-600 dark:to-gray-700 ${game.image && (game.image.startsWith('http') || game.image.startsWith('data:')) && !game.image.includes('placeholder') ? 'hidden' : 'flex'}`}>
          <div className="relative transform rotate-12">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-200 to-amber-200 dark:from-gray-500 dark:to-gray-600 rounded-lg shadow-lg border-2 border-orange-300 dark:border-gray-400 flex items-center justify-center transform perspective-500 rotateX-12 rotateY-12">
              <div className="text-2xl font-bold text-orange-600 dark:text-gray-200 transform -rotate-12">
                Z
              </div>
            </div>
          </div>
        </div>
        
        {/* Overlay Actions */}
        <div className={`absolute top-3 right-3 flex gap-1 transition-opacity duration-200 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(game)}
            className="h-8 w-8 p-0 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 shadow-sm"
            title="Edit Game"
          >
            <Edit3 size={14} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 shadow-sm"
                title="More Options"
              >
                <MoreVertical size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
              <DropdownMenuItem onClick={() => onPlay?.(game)} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20">
                <Play size={14} className="mr-2" />
                Mark as Played
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem 
                onClick={() => onToggleOwned(game.id)}
                className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                <Trophy size={14} className="mr-2" />
                {game.owned ? 'Remove from Owned' : 'Mark as Owned'}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onToggleTry(game.id)}
                className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                <Target size={14} className="mr-2" />
                {game.try ? 'Remove from Try List' : 'Want to Try'}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => {
                  // Add to wishlist logic - update game status to wishlist
                  gameService.updateGameStatus(game.id, { 
                    wishlist: true, 
                    owned: false, 
                    try: false 
                  }).then(() => {
                    // Reload the page or refresh data
                    window.location.reload();
                  });
                }}
                className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                <Bookmark size={14} className="mr-2" />
                Add to Wishlist
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Co-op Badge */}
        {isCoOp && (
          <div className="absolute top-3 left-3">
            <Badge className="bg-purple-200 dark:bg-purple-800/90 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-600 text-xs font-medium shadow-sm">
              Co-op
            </Badge>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute bottom-3 left-3">
          <Badge className={`${statusInfo.color} text-xs font-medium border cursor-default shadow-sm backdrop-blur-sm`}>
            {StatusIcon && <StatusIcon size={12} className="mr-1" />}
            {statusInfo.text}
          </Badge>
        </div>

        {/* Status Toggle Icon - Only show for non-owned games */}
        {!game.owned && (
          <div className="absolute bottom-3 right-3">
            {game.try ? (
              // If game is "Want to Try", show wishlist icon (clicking changes to wishlist)
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleOwned(game.id)} // This will change to wishlist status
                className="h-8 w-8 p-0 transition-colors text-blue-500 hover:text-blue-600 bg-white/90 dark:bg-gray-800/90"
                title="Add to Wishlist"
              >
                <Bookmark size={16} />
              </Button>
            ) : game.wishlist ? (
              // If game is "Wishlist", show want to try icon (clicking changes to want to try)
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleTry(game.id)} // This will change to try status
                className="h-8 w-8 p-0 transition-colors text-amber-500 hover:text-amber-600 bg-white/90 dark:bg-gray-800/90"
                title="Mark as Want to Try"
              >
                <Target size={16} />
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Game Title */}
        <div className="flex items-start justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-2 text-lg leading-tight">
            {game.name}
          </h3>
        </div>

        {/* Game Stats */}
        <div className="grid grid-cols-3 gap-3 py-2 border-t border-gray-100 dark:border-gray-700">
          <div className="text-center">
            <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 mb-1">
              <Users size={14} />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {game.players.min}–{game.players.max}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Players</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 mb-1">
              <Timer size={14} />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {game.playtime}m
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Playtime</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center gap-0.5 mb-1">
              {getComplexityDots(game.complexity)}
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {Math.floor(game.complexity * 10) / 10}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Complexity</div>
          </div>
        </div>

        {/* Description */}
        {game.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
            {game.description}
          </p>
        )}

        {/* Tags - Show all tags in alphabetical order */}
        <div className="flex flex-wrap gap-1.5">
          {game.tags.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((tag, index) => (
            <Badge 
              key={index}
              variant="secondary" 
              className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-orange-200 dark:hover:bg-orange-800/50 hover:text-orange-800 dark:hover:text-orange-200 transition-colors border-0 font-medium"
            >
              {tag}
            </Badge>
          ))}
        </div>

        {/* Rating and Play Count */}
        {(game.rating || game.playCount > 0) && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            {game.rating && (
              <div className="flex items-center gap-1">
                <Star size={14} className="text-orange-400 fill-current" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {Math.floor(game.rating * 10) / 10}
                </span>
              </div>
            )}
            
            {game.playCount > 0 && (
              <div className="flex items-center gap-1">
                <Play size={12} className="text-gray-400 dark:text-gray-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">{game.playCount} plays</span>
              </div>
            )}
          </div>
        )}

        {/* Price Section - Only for wishlist/want-to-try games */}
        {shouldShowPriceSection && (
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {priceData && priceData.best_price ? (
                  <div className="space-y-1">
                    {/* Find Amazon UK and Zatu prices separately */}
                    {(() => {
                      const retailers = priceData.retailers || [];
                      const amazon = retailers.find(r => r.name === 'Amazon UK');
                      const zatu = retailers.find(r => r.name === 'Zatu Games');
                      
                      return (
                        <>
                          {/* Amazon UK Price */}
                          {amazon && (
                            <div className="flex items-center gap-1">
                              <PoundSterling size={12} className="text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                {amazon.price.toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                              <a
                                href={amazon.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Amazon UK
                              </a>
                              {amazon.in_stock && (
                                <Badge variant="outline" className="text-xs px-1 py-0 text-green-600 border-green-600">
                                  In Stock
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          {/* Zatu Games Price */}
                          {zatu && (
                            <div className="flex items-center gap-1">
                              <PoundSterling size={12} className="text-purple-600 dark:text-purple-400" />
                              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                {zatu.price.toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                              <a
                                href={zatu.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Zatu Games
                              </a>
                              {zatu.in_stock && (
                                <Badge variant="outline" className="text-xs px-1 py-0 text-purple-600 border-purple-600">
                                  In Stock
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          {/* Fallback: if neither Amazon nor Zatu, show best price */}
                          {!amazon && !zatu && priceData.retailer_name && (
                            <div className="flex items-center gap-1">
                              <PoundSterling size={12} className="text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                {priceData.best_price.toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                              {(() => {
                                const bestRetailer = retailers.find(r => r.price === priceData.best_price);
                                return bestRetailer && bestRetailer.link ? (
                                  <a
                                    href={bestRetailer.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {priceData.retailer_name}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{priceData.retailer_name}</span>
                                );
                              })()}
                              {priceData.in_stock && (
                                <Badge variant="outline" className="text-xs px-1 py-0 text-green-600 border-green-600">
                                  In Stock
                                </Badge>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Price Accuracy Disclaimer and Timestamp */}
                    {priceData && (
                      <div className="mt-2 pt-1 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex flex-col gap-1">
                          {/* Timestamp */}
                          {priceData?.updated_at && (
                            <div className="flex items-center gap-1">
                              <Clock size={10} className="text-gray-400" />
                              <span className="text-xs text-gray-400">
                                Updated {new Date(priceData?.updated_at).toLocaleDateString()} {new Date(priceData?.updated_at).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                          
                          {/* Accuracy Disclaimer */}
                          <div className="flex items-start gap-1">
                            <span className="text-xs text-amber-600 dark:text-amber-400 leading-tight">
                              ⚠️ Prices may vary. Click links to verify current pricing.
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {priceData ? 'No price data' : 'Price not checked'}
                  </span>
                )}
              </div>
              
              {/* Price update button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUpdatePrice}
                disabled={isLoadingPrice || (priceCooldown && !priceCooldown.can_update)}
                className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                title={
                  priceCooldown && !priceCooldown.can_update 
                    ? `Price update available in ${priceCooldown.cooldown_remaining} minutes`
                    : 'Update price'
                }
              >
                <RefreshCw 
                  size={12} 
                  className={`${
                    isLoadingPrice ? 'animate-spin' : ''
                  } ${
                    priceCooldown && !priceCooldown.can_update 
                      ? 'text-gray-400 dark:text-gray-600' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                />
              </Button>
            </div>
            
            {priceData?.last_updated && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Updated: {new Date(priceData?.last_updated).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        )}

        {/* Last Played */}
        {game.lastPlayed && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Last played: {new Date(game.lastPlayed).toLocaleDateString()}
          </div>
        )}

        {/* Play Progress Bar */}
        {game.playCount > 0 && (
          <div className="space-y-1">
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-300 rounded-full"
                style={{ width: `${Math.min((game.playCount / 10) * 100, 100)}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {game.playCount}/10+ plays milestone
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameCard;